import { useEffect, useState } from 'react';
import {
  Sparkles,
  Zap,
  ArrowRight,
  ChevronRight,
  ArrowUpRight,
  MessageCircle,
  Radio,
  Activity,
  Wifi,
  Globe,
  Link,
  Play,
  FastForward,
  Antenna,
  Cast,
  Database,
  HardDrive,
  Layers,
  Network,
  Satellite,
  Server,
  Signal,
  Terminal,
  Upload,
  Download,
  RefreshCw,
  RotateCcw,
  Power,
  Cpu,
  MonitorSpeaker,
  Waves,
  Router,
  Rss,
  Share,
  ShareIcon,
  Podcast,
} from 'lucide-react';

interface AnimatedAiIndicatorProps {
  isLoading: boolean;
  centered?: boolean;
}

const sendIcons = [
  Zap,
  ArrowRight,
  ChevronRight,
  ArrowUpRight,
  MessageCircle,
  Radio,
  Activity,
  Wifi,
  Globe,
  Link,
  Play,
  FastForward,
];

const systemIcons = [
  Antenna, Cast, Database, HardDrive, Layers, Network, Satellite,
  Server, Signal, Terminal, Upload, Download, RefreshCw, RotateCcw,
  Power, Cpu, MonitorSpeaker, Waves, Router, Rss, Share, ShareIcon, Podcast,
];

const textGlyphs = [
  'ア', 'イ', 'ウ', 'エ', 'オ', 'カ', 'キ', 'ク', 'ケ', 'コ', 'サ', 'シ', 'ス', 'セ', 'ソ',
  'タ', 'チ', 'ツ', 'テ', 'ト', 'ナ', 'ニ', 'ヌ', 'ネ', 'ノ', 'ハ', 'ヒ', 'フ', 'ヘ', 'ホ',
  'マ', 'ミ', 'ム', 'メ', 'モ', 'ヤ', 'ユ', 'ヨ', 'ラ', 'リ', 'ル', 'レ', 'ロ', 'ワ', 'ヲ', 'ン',
  '⠀', '⠁', '⠂', '⠃', '⠄', '⠅', '⠆', '⠇', '⠈', '⠉', '⠊', '⠋', '⠌', '⠍', '⠎', '⠏',
  '⠐', '⠑', '⠒', '⠓', '⠔', '⠕', '⠖', '⠗', '⠘', '⠙', '⠚', '⠛', '⠜', '⠝', '⠞', '⠟',
  '◐', '◑', '◒', '◓', '◔', '◕', '◖', '◗', '◘', '◙', '◚', '◛', '◜', '◝', '◞', '◟',
  '▀', '▁', '▂', '▃', '▄', '▅', '▆', '▇', '█', '▉', '▊', '▋', '▌', '▍', '▎', '▏',
];

export function AnimatedAiIndicator({
  isLoading,
  centered = false,
}: AnimatedAiIndicatorProps) {
  const [currentIcon, setCurrentIcon] = useState(0);
  const [currentSystemIcon, setCurrentSystemIcon] = useState(0);
  const [currentGlyph, setCurrentGlyph] = useState(0);
  const [displayType, setDisplayType] = useState<'send' | 'system' | 'glyph'>('send');

  useEffect(() => {
    if (!isLoading) {
      setCurrentIcon(0);
      setCurrentSystemIcon(0);
      setCurrentGlyph(0);
      setDisplayType('send');
      return;
    }

    const interval = setInterval(() => {
      const random = Math.random();

      if (random > 0.7) {
        setDisplayType('glyph');
        setCurrentGlyph(Math.floor(Math.random() * textGlyphs.length));
      } else if (random > 0.4) {
        setDisplayType('system');
        setCurrentSystemIcon(Math.floor(Math.random() * systemIcons.length));
      } else {
        setDisplayType('send');
        setCurrentIcon(Math.floor(Math.random() * sendIcons.length));
      }
    }, 120);

    return () => clearInterval(interval);
  }, [isLoading]);

  const CurrentSendIcon = sendIcons[currentIcon];
  const CurrentSystemIcon = systemIcons[currentSystemIcon];
  const iconClassName = centered ? 'h-4 w-4' : 'h-3.5 w-3.5';
  const colorClassName = 'text-primary';

  if (!isLoading) {
    return (
      <Sparkles
        className={`${iconClassName} shrink-0 ${colorClassName}`}
        aria-hidden
      />
    );
  }

  if (displayType === 'send') {
    return (
      <CurrentSendIcon
        className={`${iconClassName} shrink-0 ${colorClassName} transition-all duration-75`}
        aria-hidden
      />
    );
  }

  if (displayType === 'system') {
    return (
      <CurrentSystemIcon
        className={`${iconClassName} shrink-0 ${colorClassName} transition-all duration-75`}
        aria-hidden
      />
    );
  }

  return (
    <span
      className={`${centered ? 'text-sm' : 'text-xs'} font-mono leading-none text-primary transition-all duration-75`}
      aria-hidden
    >
      {textGlyphs[currentGlyph]}
    </span>
  );
}
